"use client";

import { addUserPostalCode, getIsEuCountry } from "@/data/zipcode";
import { useCallback, useRef, useState } from "react";

import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { SpecialRow } from "@/data/specials";
import { SupabaseClient } from "@supabase/supabase-js";
import { followComedianById } from "@punchup/data/client";
import { isSubscriptionContent } from "@punchup/data/legacy";
import { logException } from "@/analytics/sentry";
import { useRouter } from "next/navigation";
import { useToast } from "@/shadcn/ui/use-toast";

/**
 * Hook para gestionar el procesamiento de pagos
 */
export const usePaymentProcessor = (
  supabase: SupabaseClient<Database>,
  special: SpecialRow,
  comedian: Comedian,
  email: string,
  fullRedirectUrl: string,
  fullCurrentPath: string,
  checkUserAlreadyHasAccess: () => Promise<boolean>,
  clearPaymentStorage: () => void,
  sendSubConfirmationEmail: (userId: string) => Promise<void>,
  getComedianProduct: (comedianId: string) => Promise<any>
) => {
  const router = useRouter();
  const { toast } = useToast();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentProcessAttempted, setPaymentProcessAttempted] = useState(false);
  const [error, setError] = useState("");

  const processedPaymentIds = useRef(new Set<string>());

  const subscriptionContent = isSubscriptionContent(special);

  const redirectToContent = (
    url: string,
    setIsProcessingPayment: (value: boolean) => void,
    specialId: string
  ) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("payment_completed", "true");
      window.localStorage.setItem("last_purchased_content", specialId);

      setTimeout(() => {
        setIsProcessingPayment(false);
        window.location.href = url;
      }, 500);
    }
  };

  const processPayment = useCallback(
    async (
      userId: string,
      paymentMethodOrIntentId: string,
      location?: { postalCode: string; countryCode: string },
      requestedToFollowComedian = false
    ) => {
      if (
        typeof window !== "undefined" &&
        window.localStorage.getItem("payment_completed")
      ) {
        return;
      }

      if (
        isProcessingPayment ||
        processedPaymentIds.current.has(paymentMethodOrIntentId)
      ) {
        return;
      }

      processedPaymentIds.current.add(paymentMethodOrIntentId);
      setIsProcessingPayment(true);
      setError("");

      toast({
        title: "Processing your payment...",
        description: "Please wait while we complete your purchase",
        variant: "default",
      });

      try {
        try {
          const hasAccess = await checkUserAlreadyHasAccess();
          if (hasAccess) {
            toast({
              title: "You already have access to this content",
              variant: "success",
            });
            clearPaymentStorage();
            redirectToContent(
              fullRedirectUrl,
              setIsProcessingPayment,
              special.id
            );
            return;
          }
        } catch (accessCheckError) {
          console.error("Error checking access:", accessCheckError);
        }

        let clientSecret = null;
        let paymentMethodId = null;
        let subscriptionId = null;
        let paymentEmail = null;

        if (typeof window !== "undefined") {
          clientSecret = window.localStorage.getItem("paymentClientSecret");
          paymentMethodId = window.localStorage.getItem("paymentMethodId");
          subscriptionId = window.localStorage.getItem("subscriptionId");
          paymentEmail = window.localStorage.getItem("paymentEmail");

          if (
            !paymentMethodId &&
            !clientSecret &&
            paymentMethodOrIntentId &&
            !paymentMethodOrIntentId.startsWith("pi_")
          ) {
            paymentMethodId = paymentMethodOrIntentId;
            window.localStorage.setItem("paymentMethodId", paymentMethodId);
          }

          if (!clientSecret || !paymentMethodId) {
            const url = new URL(window.location.href);
            const urlClientSecret = url.searchParams.get("clientSecret");
            const urlPaymentMethodId = url.searchParams.get("paymentMethodId");
            const urlSubscriptionId = url.searchParams.get("subscriptionId");

            if (urlClientSecret) {
              clientSecret = urlClientSecret;
              window.localStorage.setItem(
                "paymentClientSecret",
                urlClientSecret
              );
            }

            if (urlPaymentMethodId) {
              paymentMethodId = urlPaymentMethodId;
              window.localStorage.setItem(
                "paymentMethodId",
                urlPaymentMethodId
              );
            }

            if (urlSubscriptionId) {
              subscriptionId = urlSubscriptionId;
              window.localStorage.setItem("subscriptionId", urlSubscriptionId);
            }

            url.searchParams.delete("clientSecret");
            url.searchParams.delete("paymentMethodId");
            url.searchParams.delete("subscriptionId");
            window.history.replaceState({}, document.title, url.toString());
          }
        }

        let paymentIntentId = paymentMethodOrIntentId;

        if (clientSecret && paymentMethodId) {
          try {
            const confirmResult = await fetch("/api/stripe/confirm-payment", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                clientSecret,
                paymentMethodId,
                userId,
                email: paymentEmail || email,
                specialId: special.id,
                subscriptionId,
              }),
            });

            if (!confirmResult.ok) {
              const errorData = await confirmResult.json();
              throw new Error(errorData.error || "Failed to confirm payment");
            }

            const confirmData = await confirmResult.json();
            paymentIntentId = confirmData.paymentIntentId;

            if (!subscriptionContent && paymentIntentId) {
              if (typeof window !== "undefined") {
                window.localStorage.setItem("paymentIntentId", paymentIntentId);
              }
            }
          } catch (confirmError: any) {
            throw new Error(
              "Failed to confirm payment: " +
                (confirmError.message || "Unknown error")
            );
          }
        }

        if (
          !subscriptionContent &&
          (!paymentIntentId || !paymentIntentId.startsWith("pi_"))
        ) {
          try {
            const paymentSetupResponse = await fetch(
              "/api/stripe/payment-setup",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  special: special.slug,
                  comedianID: comedian.id,
                  subscription: false,
                  email: paymentEmail || email,
                  paymentMethodId: paymentMethodId,
                  confirm: true,
                  userId: userId,
                }),
              }
            );

            if (!paymentSetupResponse.ok) {
              const errorData = await paymentSetupResponse.json();
              console.error("Error setting up one-time payment:", errorData);
              throw new Error(
                `Failed to set up payment for one-time purchase: ${errorData.error || "Unknown error"}`
              );
            }

            const setupData = await paymentSetupResponse.json();
            if (setupData.paymentIntentId) {
              paymentIntentId = setupData.paymentIntentId;
              if (typeof window !== "undefined") {
                window.localStorage.setItem("paymentIntentId", paymentIntentId);
              }
            } else {
              throw new Error(
                "No payment intent ID returned from payment setup"
              );
            }
          } catch (setupError: any) {
            throw setupError;
          }
        }

        const paymentResponseRaw = await fetch(
          "/api/stripe/get-payment-intent",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ paymentIntentId }),
          }
        );

        if (!paymentResponseRaw.ok) {
          const errorData = await paymentResponseRaw.json();
          console.error("Error fetching payment intent:", errorData);

          if (
            paymentResponseRaw.status === 404 ||
            paymentResponseRaw.status === 400
          ) {
            clearPaymentStorage();
            redirectToContent(
              fullRedirectUrl,
              setIsProcessingPayment,
              special.id
            );
            return;
          }

          toast({
            title: "Error processing payment",
            description:
              errorData.message ||
              "The payment couldn't be verified. Please try again.",
            variant: "destructive",
          });
          setIsProcessingPayment(false);
          setPaymentProcessAttempted(false);
          return;
        }

        const paymentData = await paymentResponseRaw.json();

        if (paymentData.status === "succeeded") {
        } else if (
          ["requires_payment_method", "requires_action"].includes(
            paymentData.status
          )
        ) {
          redirectToContent(
            fullRedirectUrl,
            setIsProcessingPayment,
            special.id
          );
          return;
        }

        if (subscriptionContent) {
          try {
            let stripeSubscriptionId = paymentData.metadata?.subscription_id;

            const comedianProduct = await getComedianProduct(comedian.id);
            const { id: productID } = comedianProduct[0];

            if (!stripeSubscriptionId && typeof window !== "undefined") {
              stripeSubscriptionId =
                window.localStorage.getItem("subscriptionId");
            }

            const response = await fetch("/api/subscriptions/add-to-user", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                userId: userId,
                productId: productID,
                stripeSubscriptionId: stripeSubscriptionId,
              }),
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error("Subscription add API error:", errorText);
              toast({
                title: "Error adding subscription",
                description:
                  "We couldn't complete your subscription. Please contact support.",
                variant: "destructive",
              });
              throw new Error(`Failed to add subscription: ${errorText}`);
            }

            if (typeof window !== "undefined") {
              window.localStorage.removeItem("subscriptionId");
            }

            await sendSubConfirmationEmail(userId);
          } catch (subscriptionError) {
            console.error("Error adding subscription:", subscriptionError);
            throw subscriptionError;
          }
        } else {
          try {
            if (paymentData.status !== "succeeded") {
              try {
                if (!clientSecret) {
                  clientSecret = `${paymentIntentId}_secret_${Math.random().toString(36).substring(2)}`;
                }

                if (!paymentMethodId) {
                  if (paymentData.payment_method) {
                    paymentMethodId = paymentData.payment_method;
                  }
                }

                if (clientSecret && paymentMethodId) {
                  const confirmResponse = await fetch(
                    "/api/stripe/confirm-payment",
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        clientSecret,
                        paymentMethodId,
                        userId,
                        email: paymentEmail || email,
                        specialId: special.id,
                      }),
                    }
                  );

                  if (!confirmResponse.ok) {
                    const errorData = await confirmResponse.json();
                    console.error(
                      "Error confirming one-time payment:",
                      errorData
                    );
                    throw new Error(
                      `Failed to confirm payment: ${errorData.error || "Unknown error"}`
                    );
                  }

                  const confirmData = await confirmResponse.json();
                  if (confirmData.status !== "succeeded") {
                    // TODO: should we be handling this case?
                  }
                } else {
                  // TODO: should we be handling this case?
                }
              } catch (confirmError) {
                logException(confirmError);
                throw confirmError;
              }
            }

            const response = await fetch("/api/specials/add-to-user", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                userId: userId,
                specialId: special.id,
                paymentIntentId: paymentIntentId,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              toast({
                title: "Error adding content",
                description:
                  errorData.error ||
                  "We couldn't add this content to your account.",
                variant: "destructive",
              });
              throw new Error(
                errorData.error || "Failed to add special to user"
              );
            }
          } catch (specialError) {
            throw specialError;
          }
        }

        if (location?.postalCode) {
          try {
            await addUserPostalCode({
              supabase,
              postalCode: location.postalCode,
              countryCode: location.countryCode,
              isFromIP: false,
              isEu: getIsEuCountry(location.countryCode),
            });
          } catch (postalCodeError) {
            console.error("Error adding postal code:", postalCodeError);
          }
        }

        if (requestedToFollowComedian) {
          try {
            // Analytics tracking is handled server-side in the v2 API
            await followComedianById(
              comedian.id,
              FOLLOW_LOCATIONS.PAYMENT_FORM
            );
          } catch (followError) {
            console.error("Error following comedian:", followError);
          }
        }

        try {
          const completeResponse = await fetch("/api/stripe/complete-payment", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentIntentId: paymentIntentId,
              userId,
            }),
          });

          if (!completeResponse.ok) {
          }
        } catch (completeError) {
          logException(completeError);
        }

        if (typeof window !== "undefined") {
          window.localStorage.setItem("payment_completed", "true");
          window.localStorage.setItem("last_purchased_content", special.id);
        }

        clearPaymentStorage();
        toast({
          title: "Payment processed successfully!",
          variant: "success",
        });

        redirectToContent(fullRedirectUrl, setIsProcessingPayment, special.id);
      } catch (error) {
        logException(error);
        setError("Error processing payment. Please contact support.");
        clearPaymentStorage();

        const isBeingRedirected =
          document.visibilityState === "hidden" ||
          (typeof window !== "undefined" &&
            window.location.href !== fullCurrentPath);

        if (!isBeingRedirected) {
          toast({
            title: "Error processing payment",
            description:
              "We encountered an error processing your payment. Please try again.",
            variant: "destructive",
          });
        }

        processedPaymentIds.current.delete(paymentMethodOrIntentId);
        setIsProcessingPayment(false);
        setPaymentProcessAttempted(false);
      }
    },
    [
      isProcessingPayment,
      email,
      special,
      comedian,
      router,
      supabase,
      toast,
      fullRedirectUrl,
      fullCurrentPath,
      clearPaymentStorage,
      checkUserAlreadyHasAccess,
      sendSubConfirmationEmail,
      getComedianProduct,
    ]
  );

  return {
    processPayment,
    isProcessingPayment,
    paymentProcessAttempted,
    setPaymentProcessAttempted,
    error,
    setError,
    processedPaymentIds,
  };
};
